import angular, { IPromise, IScope } from 'angular'
import jitterTime from 'jitter-time'
import ngRedux from 'ng-redux'
import { vn } from 'vend-number'

import {
  CurrencyDetails,
  PAYMENT_TYPES,
  TabTypes,
} from '@vend/business-components'
import { ApiCustomer, ApiProduct } from '@vend/types'
import {
  VdFeatureManager,
  VdPerformanceTracking,
  VdTracking,
} from '@vend/vend.js'
import { saveToStorage, trackError } from '@vend/utilities'
import type { VdToastNotificationService } from '../../vend-ui/types'

import { STATES } from '../../../../lib/routes'
import { PaymentModalService } from '../../components/payments/payment-modal.service'
import { getPromotionById } from '../../components/promotions/promotions-resource/promotions-resource'
import { hasContactInfo } from '../../components/react/modals/common/customer-contact-utils'
import { FulfillmentOutlet } from '../../components/react/modals/mark-as-unfulfilled-modal/Steps/types'
import { STEPS } from '../../components/react/modals/service-modal/Steps/types'
import retrieveSaleTemplate from '../../components/sale/retrieve-sale/retrieve-sale-popover/retrieve-sale-popover.html'
import { WorkflowService } from '../../components/workflows/workflow.service'
import {
  DebounceOptions,
  ERRORS,
  EVENT,
  EVENTS,
  PAY,
  PERMISSIONS,
  VEND_API_LOAD_SALE_CLOSE_AFTER_COMPLETE,
} from '../../constants'
import { outletsStore } from '../../factories/database/outlets-store'
import {
  containsGiftCard,
  getBasketSize,
  isGiftCard,
} from '../../functions/lineItem'
import { PaymentManager } from '../../managers/payment-manager'
import { PosStateManager } from '../../managers/pos-state-manager'
import { PriceManager } from '../../managers/price-manager'
import { RegisterManager } from '../../managers/register-manager'
import { RetailerSettingsManager } from '../../managers/retailer-settings-manager'
import { SaleManager } from '../../managers/sale-manager'
import { SyncManager } from '../../managers/sync-manager'
import { TrainingModeManager } from '../../managers/training-mode-manager'
import { clearPaymentRequestSuccess } from '../../state/slices/posState/posStateSlice'
import { UserManager } from '../../managers/user-manager'
import { WebRegisterStatusManager } from '../../managers/web-register-status-manager'
import { FulfilmentType } from '../../models/fulfilment'
import {
  LineItem,
  LineItemInput,
  UnknownLineItem,
} from '../../models/line-item'
import { Sale } from '../../models/sale'
import { createSaleDiscount } from '../../models/sale-discount'
import { SaleStatus } from '../../models/sale.constants'
import {
  $interval,
  $log,
  $q,
  $rootScope,
  $state,
  $stateParams,
  $timeout,
} from '../../ng-compat/imports'
import {
  ReduxCurrentSale,
  removeUnknownLineItem,
  selectCurrentSale,
  selectCurrentSaleAsSale,
  selectFulfilmentNoteIsInvalid,
  selectLineItems,
  selectNoteIsInvalid,
  selectSaleAttributesHasFulfilment,
  selectUnknownLineItems,
  setSaleNote,
  updateCalculatingStateOfAllLineItems,
  updateInitialPaidBalance,
  updateLineItem,
  updateSaleDiscount,
  writeMissingSaleId,
} from '../../state/slices/currentSale/currentSaleSlice'
import { RootState } from '../../state/store'
import { ERROR_TYPES, SERVICE_REFUND_ACTIONS } from '../../util/types'
import {
  isReturn,
  isClosed,
  isOpen,
  isLayby,
  isParked,
  isOnAccount,
  isQuote,
  isLegacyServiceSale,
  getServiceAgendaBannerText,
  isValidCustomer,
  getOutstandingBalance,
  isConfirmedNotReadyToComplete,
  isConfirmedReadyToComplete,
  isMarkedAsUnfulfilled,
  hasPayments,
  hasLineItems,
  isBlindReturn,
  hasUnknownLineItems,
  isAccountBalancePaymentAdded,
  hasCustomerRelatedPayments,
  isUnfulfilledPickUp,
  getPaymentsTotal,
  hasDiscount,
} from '../../functions/sale'
import { hasServiceOrder } from '../../functions/service'
import { BalancesGiftCardsModal } from '../../modals/gift-cards-modal'
import {
  resetLineItemInputErrors,
  selectHasQuantityInputError,
  selectIsAReturnWithoutPermission,
  selectIsFulfilmentPopoverOpen,
} from '../../state/slices/uiStateSlice'
import { isMissingContactInfo } from '../../components/sale/sale-line-item/fulfilment/popover/LineItemFulfillmentUtils'
import type { RegisterModals } from '../../modals/register-modals'
import { clientApiEventBus } from '../../managers/client-api-event-bus'
import { SaleTransformer } from '../../factories/sale-transformer'

const expectedRegisterClosurePollTime = jitterTime(5)

const COMPLETE_SALE_LABELS = {
  BALANCE: 'Balance',
  EXCHANGE: 'Exchange',
  PAY: 'Pay',
  REFUND: 'Refund',
}

const PARTIALLY_PAID_MESSAGE =
  'The current sale has been partially paid. The customer can not be changed.'

type MoreActionsItemName = 'QUOTE' | 'SERVICE' | 'DISCARD' | 'UNFULFILLED'

/**
 * =======================================
 * SALE CONTROLLER
 * =======================================
 */
class SaleCtrl {
  public readonly customerErrorTemplate: string
  public readonly customerSource: any
  public readonly readOnlyCustomerSource: any
  public hasActiveSale: boolean
  public isFinalisingSale = false
  public isInitialising = true
  public saleCalculating = false
  public saleForm!: any
  public showRegisterOpenReminder = false
  public shouldPromptForNote = true
  public debounceOptions: DebounceOptions | undefined
  public serviceModalSteps = STEPS
  public retrieveParkedSale: (saleid: string) => void

  private currentSale: ReduxCurrentSale
  private expectedRegisterClosureInterval?: IPromise<any> | null
  private waitingForWorkflows = false
  private loadingMarkUnfulfilledModal = false
  private shouldOpenServiceModal = true
  private shouldOpenServiceSaleRefundModal = true
  private hasLspayPaymentType: boolean = false

  constructor(
    private readonly $scope: IScope,
    private readonly $ngRedux: ngRedux.INgRedux,
    private readonly priceManager: PriceManager,
    private readonly saleManager: SaleManager,
    private readonly posStateManager: PosStateManager,
    private readonly registerManager: RegisterManager,
    private readonly saleLoader: any,
    private readonly customerModals: any,
    private readonly productModals: any,
    private readonly saleModals: any,
    private readonly wrSaleStatusFilter: any,
    private readonly userSwitchingService: any,
    private readonly workflowService: WorkflowService,
    private readonly balancesGiftCardsModal: BalancesGiftCardsModal,
    private readonly customerManager: any,
    private readonly vdToastNotificationService: VdToastNotificationService,
    private readonly trainingModeManager: TrainingModeManager,
    private readonly vdTracking: VdTracking,
    private readonly registerModals: RegisterModals,
    private readonly syncManager: SyncManager,
    customerSuggestions: any,
    private readonly vdPerformanceTracking: VdPerformanceTracking,
    private readonly webRegisterStatusManager: WebRegisterStatusManager,
    /** @deprecated use `getFeatureFromCache` in `assets/scripts/util/feature-cache.ts` instead */
    private readonly vdFeatureManager: VdFeatureManager,
    private readonly userManager: UserManager,
    private readonly vdPopover: any,
    private readonly promotionModalService: any,
    private readonly retailerSettingsManager: RetailerSettingsManager,
    private readonly markUnfulfilledModalService: any,
    private readonly saleTracker: any,
    private readonly quoteCustomerModalService: any,
    private readonly serviceModalService: any,
    private readonly serviceSaleRefundModalService: any,
    private readonly paymentModalService: PaymentModalService,
    private readonly paymentManager: PaymentManager,
    private readonly saleTransformer: SaleTransformer
  ) {
    'ngInject'

    this.currentSale = selectCurrentSale(this.$ngRedux.getState())
    this.customerSource = customerSuggestions.getSource()
    this.readOnlyCustomerSource = customerSuggestions.getSource({
      readOnly: true,
    })
    this.retrieveParkedSale = this.onRetrieveSale.bind(this)
    // Replaces the default error message for the "add customer to sale" autocomplete
    this.customerErrorTemplate = customerSuggestions.customerErrorTemplate

    // Track whether we have an active sale to determine whether to enable the "New Sale" (void) button
    this.hasActiveSale = Boolean(this.posStateManager.getActiveSaleLocalId())

    this.debounceOptions = this.saleManager.debounceOptions

    // Fetching Lspay payment type availability
    this.checkLspayPaymentTypeAvailability()

    // bind it here to avoid TypeError: Cannot read properties of undefined
    this.moreActionsItemHandler = this.moreActionsItemHandler.bind(this)
    this.init()
  }

  private init() {
    // Connect to Redux store
    const disconnectRedux = this.$ngRedux.connect((state: RootState) => ({
      currentSale: selectCurrentSale(state),
    }))(this)

    // Clear any payment request success flags when entering the main sale screen
    this.$ngRedux.dispatch(clearPaymentRequestSuccess())

    const onActiveSaleLocalIdChange = (event: any) => {
      this.hasActiveSale = Boolean(event.newValue)
    }

    this.posStateManager.on(
      'activeSaleLocalIdChange',
      onActiveSaleLocalIdChange,
      this
    )

    // Event handlers as arrow functions to bind the right "this".
    const pollForExpectedRegisterClosure = () =>
      this.pollForExpectedRegisterClosure()

    const cancelPollForExpectedRegisterClosure = () =>
      this.cancelPollForExpectedRegisterClosure()

    const toggleExpectedRegisterClosurePoll = () =>
      this.toggleExpectedRegisterClosurePoll()

    this._init().then(() => {
      const action = $stateParams.action

      // Wait for the sale to be loaded, then determine if in the good state to be in this view.
      if (
        this.currentSale &&
        (isClosed(this.currentSale.status) ||
          isConfirmedReadyToComplete(this.currentSale) ||
          isQuote(this.currentSale.status))
      ) {
        $state.go(STATES.SALE_CONFIRMATION)
      } else if (action && action === PAY) {
        // Recalculate the sale before attempting to pay for it
        this.saleManager
          .queueSaleCalculation()
          .then(() => this.readyForPayment())
      }

      toggleExpectedRegisterClosurePoll()
    })

    // Listen for remove line item event
    this.$scope.$on(
      'sale:remove-line-item',
      this.handleRemoveLineItem.bind(this)
    )

    const rootScopeListeners: Array<() => void> = []

    this.posStateManager.on(
      'registerOpenRemindersSeenChange',
      () => this.isRegisterOpenReminderNeeded(),
      this
    )
    this.registerManager.on(
      'activeRegisterOpen',
      pollForExpectedRegisterClosure,
      this
    )
    this.registerManager.on(
      'activeRegisterClose',
      cancelPollForExpectedRegisterClosure,
      this
    )
    this.registerManager.on(
      'activeRegisterChange',
      pollForExpectedRegisterClosure,
      this
    )
    this.trainingModeManager.on(
      'trainingModeEnabledChange',
      toggleExpectedRegisterClosurePoll
    )
    this.webRegisterStatusManager.on(
      EVENTS.statusChange,
      toggleExpectedRegisterClosurePoll,
      this
    )

    rootScopeListeners.push(
      // Listen for product selected event
      $rootScope.$on(
        EVENT.SEARCH_PRODUCT_SELECTED,
        this.handleProductSelected.bind(this)
      ),
      // Listen for product selected event
      $rootScope.$on(
        'search:embedded-barcode-selected',
        this.handleEmbeddedBarcodeSelected.bind(this)
      ),
      // Listen for unknown products selected
      $rootScope.$on(
        'search:unknown-product',
        this.handleUnknownProductSelected.bind(this)
      ),
      // Listen for customer selected event
      $rootScope.$on(
        'search:customer-selected',
        this.handleCustomerSelected.bind(this)
      ),
      // Listen for load sale request from the "retrieve sale" popover
      $rootScope.$on('retrieveSale:loadSale', (event, saleId) =>
        this.onRetrieveSale(saleId)
      ),
      // Reset sale form validity when a new sale is created
      $rootScope.$on('sale:new', this.resetSaleForm.bind(this))
    )

    // Listen for the sale calculating complete event
    const setCalculationComplete = () => this.setSaleCalculatingState(false)
    this.saleManager.on(
      'saleManager:saleCalculating:end',
      setCalculationComplete
    )

    // On destroy, we must destroy $rootScope listeners
    this.$scope.$on('$destroy', () => {
      this.posStateManager.off(
        'activeSaleLocalIdChange',
        onActiveSaleLocalIdChange
      )
      this.posStateManager.off('registerOpenRemindersSeenChange', () =>
        this.isRegisterOpenReminderNeeded()
      )
      this.registerManager.off(
        'activeRegisterOpen',
        pollForExpectedRegisterClosure
      )
      this.registerManager.off(
        'activeRegisterClose',
        cancelPollForExpectedRegisterClosure
      )
      this.registerManager.off(
        'activeRegisterChange',
        pollForExpectedRegisterClosure
      )
      this.trainingModeManager.off(
        'trainingModeEnabledChange',
        toggleExpectedRegisterClosurePoll
      )
      this.webRegisterStatusManager.off(
        EVENTS.statusChange,
        toggleExpectedRegisterClosurePoll
      )

      rootScopeListeners.forEach(unbindListener => unbindListener())
      cancelPollForExpectedRegisterClosure()
      this.saleManager.off(
        'saleManager:saleCalculating:end',
        setCalculationComplete
      )

      disconnectRedux()
    })
  }

  /**
   * Initialise the controller ensuring the saleManager is initialised and the appropriate Sale is loaded.
   *
   * @return {Promise} A promise resolved once initialisation is complete
   */
  private async _init() {
    let saleResolver
    const quoteId = $stateParams.quoteId

    if (quoteId) {
      // use quoteId for traceability. loadSale can actually accept any string
      saleResolver = this.loadSale(quoteId)
    } else {
      saleResolver = $stateParams.sale
        ? this.loadSale()
        : this.saleManager.init()
    }

    return saleResolver.finally(() => {
      if ($stateParams.closeAfterComplete) {
        saveToStorage(VEND_API_LOAD_SALE_CLOSE_AFTER_COMPLETE, {
          closeAfterComplete: $stateParams.closeAfterComplete,
        })
      }
      this.checkAndOpenLSPayRecoveryModal()

      this.isInitialising = false
    })
  }

  public openCustomerUpdateModal() {
    return this.customerModals.updateCustomer(this.currentSale.customer)
  }

  /**
   * Open up the promotion modal
   */
  public openPromotionModal = (promotionId: string) => {
    this.posStateManager.setPromotionsHelperClicked()
    return $q
      .all({
        promotion: $q.resolve(getPromotionById(promotionId)),
        groups: this.customerManager.getCustomerGroups(),
        productIds: this.saleManager.getLineItemProductIds(),
        retailer: this.retailerSettingsManager.get(),
      })
      .then(data => {
        if (!data.promotion) {
          return
        }
        const currencyDetails: CurrencyDetails = {
          culture: data.retailer.culture,
          code: data.retailer.currency.code,
          symbol: data.retailer.currency.symbol,
        }
        return this.promotionModalService.openPromotion({
          promotion: data.promotion,
          lineItemIds: data.productIds,
          currencyDetails,
          isTaxExclusive: !this.registerManager.isTaxInclusive(),
          totalCustomerGroups: data.groups ? data.groups.length : 0,
          defaultTab: TabTypes.Buy,
        })
      })
  }

  /**
   * Responds to the `retrieveSale:loadSale` event which requests the retrieval a sale while on the sell screen.
   *
   * @param {String} saleId
   *        The id of the sale to load
   *
   * @returns {Promise} Resolved once the sale has been loaded.
   */
  private onRetrieveSale(saleId: string) {
    return this.saleManager
      .actionExistingSaleIfRequired({ saleToLoad: saleId })
      .then(response => {
        if (response.hasActiveSale) {
          // Still have an active sale, re-focus the global search and otherwise do nothing
          this.focusGlobalSearch()
          return null
        } else {
          // Initialise new sale
          this.isInitialising = true

          return this.loadSale(saleId)
            .then(() => {
              // For trouble shooting: Missing sale id in retrieved sale and
              // trigger overwriting sale rejection when calling createSaleMutation
              if (!this.currentSale.id) {
                this.vdTracking.trackUserEvent({
                  eventName: 'Sale id missing in retrieved sale',
                  metadata: {
                    saleId: this.currentSale.localId,
                  },
                })
                // Manually write sale.id back
                this.$ngRedux.dispatch(writeMissingSaleId())
              }
              this.focusGlobalSearch()
            })
            .finally(() => {
              this.isInitialising = false
            })
        }
      })
  }

  /**
   * Load the Sale specified in the $stateParams and determine whether it is
   * appropriate to set on the sale manager (not voided or completed, deal with
   * a closed register, deal with  an existing sale that needs to be parked/
   * voided/etc).
   */
  private loadSale(
    sale?:
      | string
      | {
          id: string
        }
      | {
          localId: string
        }
  ) {
    const saleToLoad = sale || $stateParams.sale

    const saleIdToLoad = saleToLoad.id || saleToLoad.localId || saleToLoad
    return $q
      .when(this.userSwitchingService.switchUserIfNeeded())
      .then(() => this.syncManager.sync({ skipEmitSyncComplete: true }))
      .then(() => this.saleLoader.loadSale(saleIdToLoad))
      .then((loadedSale: Sale) => {
        // Ensure register is open
        return this.registerModals
          .openRegisterToContinueSaleIfNeeded()
          .then(() => loadedSale)
          .catch(() => null)
      })
      .then((loadedSale: Sale | null) => {
        // Confirm that we have an open sale with a valid customer before attempting to refresh the
        // customer details
        if (
          loadedSale &&
          isOpen(loadedSale.status) &&
          isValidCustomer(loadedSale.customer)
        ) {
          $q.when(this.customerManager.refreshCustomer(loadedSale.customer))
        }
        return loadedSale
      })
      .then((loadedSale: Sale | null) => {
        // Wait until here to init saleManager as saleManager.init() waits for an open register and we may
        // have had to prompt the user to open the register if they were attempting to retrieve a sale
        return this.saleManager.init().then(() => {
          if (!loadedSale) {
            // Sale load cancelled due to closed register
            return
          }

          return (
            this.exitTrainingModeIfNeeded()
              .then(() => this.checkOutletOfLoadedSale(loadedSale))
              .then(() =>
                this.saleManager.actionExistingSaleIfRequired({
                  saleToLoad: saleIdToLoad,
                })
              )
              .then((response: { hasActiveSale: boolean }) => {
                // Ensure that we don't change the sync status of a loaded, on account or layby or unfulfilled
                // sale from "unmodified" to "modified" when setting the active sale.
                const saleModificationOptions = {
                  unconfirmedSalesOnly:
                    isConfirmedNotReadyToComplete({
                      status: loadedSale.status as SaleStatus,
                      readyToComplete: loadedSale.readyToComplete,
                      isSubmittedSaleEarly: loadedSale.isSubmittedSaleEarly,
                    }) || Boolean(loadedSale.isSavedUnfulfilled),
                }

                if (response.hasActiveSale) {
                  // Still have an active sale, continue without loading the sale
                  return null
                } else {
                  return this.saleManager.setActiveSale(
                    loadedSale,
                    saleModificationOptions
                  )
                }
              })
              // Modal cancelled, continue without loading the sale
              .catch(() => null)
          )
        })
      })
      .then(() => {
        if (
          this.currentSale &&
          !isValidCustomer(this.currentSale.customer) &&
          isReturn(this.currentSale)
        ) {
          return (
            this.customerModals
              .addCustomerToSale({ cancelButtonLabel: 'Skip' })
              // Cancelling the modal rejects the promise but we don't want that to be considered an error, so regardless
              // we resolve to the loaded sale
              .then(
                () => this.currentSale,
                () => this.currentSale
              )
          )
        }
      })
      .then(() => {
        if (this.currentSale && isParked(this.currentSale.status)) {
          this.saleManager.saleCalculationQueue()
        }
        if (
          this.currentSale &&
          (isLayby(this.currentSale.status) ||
            isOnAccount(this.currentSale.status))
        ) {
          this.$ngRedux.dispatch(
            updateInitialPaidBalance(getOutstandingBalance(this.currentSale))
          )
        }
      })
      .catch((error: any) => {
        let errorType = error && error.type
        let message

        if (!errorType && error.status === 404) {
          errorType = ERROR_TYPES.LOAD_SALE_ABORTED_NOT_FOUND
        }

        switch (errorType) {
          case ERROR_TYPES.LOAD_SALE_ABORTED_NOT_FOUND:
            message = 'This sale can’t be found - please contact support.'
            break

          case ERROR_TYPES.LOAD_QUOTE_ABORTED_NOT_FOUND:
            message = 'Failed to retrieve quote.'
            break

          case ERROR_TYPES.LOAD_QUOTE_ABORTED_ARCHIVED:
            message = 'Cannot convert an archived quote to a sale'
            break

          case ERROR_TYPES.LOAD_QUOTE_ABORTED_NO_CUSTOMER:
            message = 'Cannot convert a quote to a sale without a customer'
            break

          case ERROR_TYPES.LOAD_QUOTE_ABORTED_NO_PRODUCTS:
            message = 'Cannot convert a quote without items to a sale'
            break

          case ERROR_TYPES.LOAD_SALE_ABORTED_VOIDED:
            message = 'This sale can’t be opened as it has been discarded.'
            break

          case ERROR_TYPES.LOAD_SALE_ABORTED_COMPLETED:
            message = 'This sale has already been completed.'
            break

          case ERROR_TYPES.LOAD_SERVICE_ABORTED_INCOMPLETE:
            message =
              'Incomplete service. Continue the sale from sales history to proceed.'
            break

          case ERROR_TYPES.LOAD_SERVICE_ABORTED_AGENDA_NOT_FOUND:
            message = 'Failed to retrieve service agenda'
            break

          case ERROR_TYPES.LOAD_SERVICE_ABORTED_SERVICE_ITEM_NOT_FOUND:
            message = 'Failed to retrieve service item'
            break
          default:
            trackError('[SaleCtrl] Unknown error loading sale', {
              data: { saleToLoad, error },
            })
            message = 'This sale failed to load, please try again.'
            break
        }
        this.vdToastNotificationService.negative({ message })

        // Ensure we at least attempt to initialise the saleManager so we don't end up with a null/undefined sale
        return this.saleManager.init()
      })
      .finally(() => this.setSaleCalculatingState(false))
  }

  /**
   * Determine if the sale action buttons should be disabled.
   *
   * @return {Boolean} True if the sale should be disabled, false otherwise.
   */
  public shouldDisableActions() {
    const isSaleNoteInvalid = selectNoteIsInvalid(this.$ngRedux.getState())
    const isFulfilmentNoteInvalid = selectFulfilmentNoteIsInvalid(
      this.$ngRedux.getState()
    )

    const returnWithoutPermission = selectIsAReturnWithoutPermission(
      this.$ngRedux.getState()
    )

    return Boolean(
      !this.hasActiveSale ||
        (!isConfirmedNotReadyToComplete(this.currentSale) &&
          !this.currentSale.isSavedUnfulfilled &&
          !this.currentSale.isSavedService &&
          hasPayments(this.currentSale)) ||
        this.trainingModeManager.trainingModeEnabled ||
        // This check is needed as the sale form may not have been initialised yet
        (this.saleForm &&
          this.saleForm.$invalid &&
          !this.saleOnlyHasNoInventoryError()) ||
        this.isFinalisingSale ||
        this.saleCalculating ||
        this.loadingMarkUnfulfilledModal ||
        this.saleHasUnknownLineItems() ||
        returnWithoutPermission ||
        isSaleNoteInvalid ||
        isFulfilmentNoteInvalid
    )
  }

  /**
   *Determine if noInventory is the only form error
   *
   * @return {Boolean} True if saleForm only contains noInventory error, false otherwise.
   */
  public saleOnlyHasNoInventoryError() {
    const keys = this.saleForm.$error && Object.keys(this.saleForm.$error)
    return Boolean(keys && keys.length === 1 && keys[0] === ERRORS.NO_INVENTORY)
  }

  /**
   * Handle click event on more actions dropdown item
   * @param {MoreActionsItemName} item - name of the item clicked
   */
  public moreActionsItemHandler(item: MoreActionsItemName) {
    switch (item) {
      case 'QUOTE': {
        this.quoteSale()
        break
      }
      case 'SERVICE': {
        this.vdTracking.trackUserEvent({
          moduleName: 'Services',
          eventName: 'sale.service_sale.new_sale.create_service.click',
        })
        this.createService()
        break
      }
      case 'DISCARD': {
        this.discardSale()
        break
      }
      case 'UNFULFILLED': {
        this.markAsUnfufilled(true)
        break
      }
    }
  }

  /**
   * Get all line items including unknown line items for the sale
   * This is mainly only used for calling from the angular template
   */
  public getAllLineItems() {
    const lineItems = selectLineItems(this.$ngRedux.getState())
    const unknownLineItems = selectUnknownLineItems(this.$ngRedux.getState())
    return [...lineItems, ...unknownLineItems]
  }

  /**
   * Open mark as unfulfilled modal and handle modal close
   */
  public markAsUnfufilled = (fromDropdown: boolean) => {
    const lineItems = selectLineItems(this.$ngRedux.getState())
    if (containsGiftCard(lineItems)) {
      this.vdToastNotificationService.negative({
        message:
          "Gift cards can't be marked for fulfillment. Remove the gift card, then try again.",
      })
      return
    }

    this.vdTracking.trackUserEvent({
      eventName: 'Opened Mark Sale as Unfulfilled Modal',
      metadata: {
        saleId: this.currentSale.localId,
        customerAttached: isValidCustomer(this.currentSale.customer),
        openedFrom: fromDropdown
          ? 'moreActionsDropdown'
          : 'negativeInventoryBanner',
      },
    })

    this.openMarkUnfulfilledModal()
      .then((modal: any) => modal.closed)
      .then((hasMarked: boolean) => {
        this.focusGlobalSearch()
      })
  }

  /**
   * Open up the mark as unfulfilled modal
   */
  public openMarkUnfulfilledModal() {
    this.saleTracker.addTrackingData({
      customerAttached: isValidCustomer(this.currentSale.customer),
    })
    if (this.vdFeatureManager.get('fulfill_from_another_outlet')) {
      this.loadingMarkUnfulfilledModal = true
      return outletsStore
        .all()
        .then(outlets => {
          const allOutlets: FulfillmentOutlet[] = []
          outlets.forEach(outlet => {
            allOutlets.push({ id: outlet.id, name: outlet.name })
          })
          this.loadingMarkUnfulfilledModal = false
          return this.markUnfulfilledModalService.markUnfulfilled(allOutlets)
        })
        .catch((error: any) => {
          this.loadingMarkUnfulfilledModal = false
          this.vdToastNotificationService.negative({
            message:
              "Sorry, we couldn't begin the fulfillment process. Please try again.",
          })
          trackError(
            '[SaleCtrl] Failed to fetch all outlets for unfulfilled modal',
            {
              data: { error },
            }
          )
        })
    } else {
      return this.markUnfulfilledModalService.markUnfulfilled([])
    }
  }

  /**
   * Handle "add customer to quote" modal open and close
   *
   * @returns
   */

  public openQuoteCustomerModal() {
    this.vdTracking.trackUserEvent({
      moduleName: 'Quotes',
      eventName: 'Opened: Add customer and note to quote modal',
    })
    return this.quoteCustomerModalService
      .quoteCustomerModal()
      .then((modal: any) => modal.closed)
      .then(() => {
        if (hasContactInfo(this.currentSale.customer)) {
          // quote sale on close only if there is a valid + with contact info customer
          this.parkOrQuoteSale(false)
        } else {
          this.focusGlobalSearch()
        }
      })
  }

  /**
   * Check for active LSPay payment and open the recovery modal
   */
  public checkAndOpenLSPayRecoveryModal() {
    const {
      registerSalePaymentId,
    } = this.paymentManager.checkAndRetriveActivePayment(
      this.currentSale.localId
    )
    if (!registerSalePaymentId) {
      // No active payment, no need to show the modal
      return
    }
    return this.paymentModalService
      .lspayRecovery(this.currentSale.localId)
      .then((modal: any) => modal.closed)
      .then(() => {
        this.transitionToPaymentScreen()
      })
  }

  /**
   * Handle "add customer to service order" modal open and close
   * @param isEditing - whether the service order is being edited
   * @param startWithStep - the step to start the modal with (used to skip customer step when editing a service)
   * @returns {Promise} - resolves when the modal is closed and the service order is created
   */
  public openServiceModal = ({
    isEditing = false,
    startWithStep = STEPS.SELECT_CUSTOMER,
  }: { isEditing?: boolean; startWithStep?: STEPS } = {}) => {
    if (isEditing) {
      this.vdTracking.trackUserEvent({
        moduleName: 'Services',
        eventName: 'sale.service_sale.new_sale.edit_schedule.click',
      })
    } else {
      startWithStep = STEPS.SELECT_CUSTOMER
    }

    return this.serviceModalService
      .serviceModal(startWithStep, isEditing)
      .then((modal: any) => modal.closed)
      .then((shouldCreate = false) => {
        if (shouldCreate && hasContactInfo(this.currentSale.customer)) {
          this.setShouldOpenServiceModal(false)
          this.createService()
        }
        this.focusGlobalSearch()
      })
  }

  /**
   * Determine if the promotion suggestion banner should be shown.
   */
  public shouldShowPromoBanner() {
    return Boolean(
      !this.isInitialising &&
        this.syncManager.hasActivePromotions &&
        this.hasLineItems() &&
        this.currentSale.potentialPromotionId &&
        !this.shouldShowSaleErrorBanner() &&
        !this.shouldShowMarkUnfulfilledBanner() &&
        !this.shouldShowUnfulfilledBanner() &&
        !this.shouldShowServiceBanner()
    )
  }

  /**
   * Helper to determine if the current sale has any unknown line items.
   */
  public saleHasUnknownLineItems() {
    return Boolean(this.currentSale && hasUnknownLineItems(this.currentSale))
  }

  /**
   * Determine if the sale error banner should be shown.
   */
  public shouldShowSaleErrorBanner() {
    return Boolean(!this.isInitialising && this.saleHasUnknownLineItems())
  }

  /**
   * Determine if the blind return banner should be shown.
   */
  public shouldShowBlindReturnBanner() {
    return Boolean(
      !this.isInitialising &&
        this.currentSale &&
        this.userManager.hasPermission(PERMISSIONS.SALE_RETURN) &&
        isBlindReturn(this.currentSale) &&
        !this.shouldShowSaleErrorBanner() &&
        !this.shouldShowPromoBanner() &&
        !isMarkedAsUnfulfilled(this.currentSale.status) &&
        !isLegacyServiceSale(this.currentSale.status)
    )
  }

  /**
   * Determine if the mark unfulfilled banner should be shown.
   */
  public shouldShowMarkUnfulfilledBanner() {
    return Boolean(
      !this.vdFeatureManager.get('fulfillment_lif_b3') &&
        !this.isInitialising &&
        this.currentSale &&
        !isMarkedAsUnfulfilled(this.currentSale.status) &&
        this.hasLineItems() &&
        !this.shouldShowSaleErrorBanner() &&
        this.saleOnlyHasNoInventoryError() &&
        this.saleManager.canMarkAsUnfulfilled() &&
        !this.loadingMarkUnfulfilledModal &&
        !isLegacyServiceSale(this.currentSale.status)
    )
  }

  /**
   * Determine if the sale is unfulfilled banner should be shown.
   */
  public shouldShowUnfulfilledBanner() {
    return Boolean(
      !this.vdFeatureManager.get('fulfillment_lif_b3') &&
        !this.isInitialising &&
        this.currentSale &&
        !this.shouldShowSaleErrorBanner() &&
        isMarkedAsUnfulfilled(this.currentSale.status)
    )
  }

  /**
   * Determine if service banner should be shown
   */
  public shouldShowServiceBanner() {
    if (this.isInitialising || !this.currentSale) {
      return false
    }

    const shouldShowForServices =
      hasServiceOrder(
        this.currentSale.serviceFields,
        this.currentSale.customFields
      ) || isLegacyServiceSale(this.currentSale.status)

    return Boolean(!this.shouldShowSaleErrorBanner() && shouldShowForServices)
  }

  /**
   * Get the service agenda banner text
   */
  public getServiceAgendaBannerText = () => {
    const sale = selectCurrentSale(this.$ngRedux.getState())
    return getServiceAgendaBannerText(sale)
  }

  /**
   * Manage the service sale refund modal informing user that the service sale won't be editable after refund
   */
  public openServiceSaleRefundModal() {
    this.serviceSaleRefundModalService
      .serviceSaleRefundModal()
      .then((modal: any) => {
        return modal.closed.then((data?: string) => {
          switch (data) {
            case SERVICE_REFUND_ACTIONS.REFUND:
              this.vdTracking.trackUserEvent({
                moduleName: 'Services',
                eventName:
                  'services.service_sale.continue_sale.refund_sale_modal.continue_refund.click',
              })
              this.setShouldOpenServiceRefundSaleModal(false)
              this.transitionToPaymentScreen()
              break

            case SERVICE_REFUND_ACTIONS.DISMISS:
              this.vdTracking.trackUserEvent({
                moduleName: 'Services',
                eventName:
                  'services.service_sale.continue_sale.refund_sale_modal.dismiss_sale.click',
              })
              this.saleManager.clearSale()
              break
          }
        })
      })
  }

  /**
   * Set the value of the 'shouldOpenServiceModal' flag,
   * determining whether the 'service customer modal' should be opened
   * when a 'service order' is created with a linked customer
   *
   * @param {Boolean} value
   * @return {void}
   *
   */
  private setShouldOpenServiceModal(value: boolean) {
    this.shouldOpenServiceModal = value
  }

  /**
   * Set the value of the 'setShouldOpenServiceRefundSaleModal' flag,
   * determining whether the 'service refund sale modal' should be opened
   * when a 'service order' is created with a linked customer and the service items can be refunded
   *
   * @param {Boolean} value
   * @return {void}
   *
   */
  private setShouldOpenServiceRefundSaleModal(value: boolean) {
    this.shouldOpenServiceSaleRefundModal = value
  }

  /**
   * Gets the correct label to display for the sale button
   */
  public getCompleteSaleLabel() {
    if (!this.currentSale) {
      return COMPLETE_SALE_LABELS.PAY
    }

    // Determine what should be shown as the complete sale button
    if (
      isConfirmedNotReadyToComplete(this.currentSale) ||
      hasPayments(this.currentSale)
    ) {
      const balance = getOutstandingBalance(this.currentSale)
      if (vn(balance).lt(0)) {
        return COMPLETE_SALE_LABELS.REFUND
      } else {
        return COMPLETE_SALE_LABELS.PAY
      }
    } else if (this.currentSale.displayStatus === SaleStatus.RETURN_PARKED) {
      return COMPLETE_SALE_LABELS.REFUND
    } else if (this.currentSale.displayStatus === SaleStatus.EXCHANGE_PARKED) {
      return COMPLETE_SALE_LABELS.EXCHANGE
    }
    return COMPLETE_SALE_LABELS.PAY
  }

  /**
   * Handler for when a customer is selected from the autocomplete suggestions.
   *
   * @param {Object} params
   *
   *        @param {Customer} params.customer
   *               The selected customer
   */
  public onCustomerSelect({ customer }: { customer: ApiCustomer }) {
    this.saleManager.addCustomer(customer).then(() => {
      // This is necessary so that training mode can advance the training tour to the next step
      $rootScope.$emit('autocomplete:customer-selected')

      // Focus the product search field as the customer search field is now hidden by the selected customer details
      $rootScope.$emit('autocomplete:global-search:focus')
    })
  }

  /**
   * Park a sale
   */
  public parkSale() {
    this.parkOrQuoteSale(true)
  }

  /**
   * Quote a sale
   */
  public quoteSale() {
    this.vdTracking.trackUserEvent({
      moduleName: 'Quotes',
      eventName: 'Clicked: Create Quote',
    })
    if (!hasContactInfo(this.currentSale.customer)) {
      this.openQuoteCustomerModal()
    } else {
      this.parkOrQuoteSale(false)
    }
  }

  /**
   * Create service order sale
   */
  public createService() {
    if (!hasContactInfo(this.currentSale.customer)) {
      this.openServiceModal()
    } else if (this.shouldOpenServiceModal) {
      this.openServiceModal()
    } else {
      // Reset shouldOpenServiceModal to true so that the modal will open again
      // if the user clicks on the "Create service" button
      this.setShouldOpenServiceModal(true)
    }
  }

  /**
   * Park or quote the sale; prompts a user for a note if one has not already
   * been entered. Also shows the user switching modal if needed.
   */
  public parkOrQuoteSale(isParkSale: boolean) {
    if (this.saleManager.saleCalculating) {
      this.setSaleCalculatingState(true)
      return
    }

    const lineItems = selectLineItems(this.$ngRedux.getState())

    if (containsGiftCard(lineItems)) {
      this.vdToastNotificationService.negative({
        message: isParkSale
          ? `Sales with gift cards can't be parked. Remove the gift card, then park the sale.`
          : `Quotes can’t include gift cards. Remove the gift card and try again.`,
      })
      return
    }

    const register = this.registerManager.getActiveRegister()
    const registerNotePreference = register && register.ask_for_note_on_save
    this.shouldPromptForNote =
      isParkSale &&
      (registerNotePreference === 2 || registerNotePreference === 1)

    const parkSaleResolver = this.shouldPromptForNote
      ? this.saleModals.actionAddNoteParkSale(
          this.currentSale.note,
          isParkSale,
          isReturn(this.currentSale) || isBlindReturn(this.currentSale),
          isAccountBalancePaymentAdded(this.currentSale.payAccountBalance)
        )
      : $q.resolve({
          note: this.currentSale.note,
        })
    this.isFinalisingSale = true

    // Prompt for a note before transitioning to a parked sale state. Allows the modal to be cancelled and for the sale
    // to remain open with it's sale note unchanged.
    parkSaleResolver
      .then(({ note, isQuote }: { note: string; isQuote: boolean }) => {
        this.$ngRedux.dispatch(setSaleNote(note))

        if (!isParkSale) {
          return this.saleManager
            .quoteSale()
            .then(() => $state.go(STATES.SALE_CONFIRMATION))
        } else {
          return this.finaliseParkSale()
        }
      })
      .catch(angular.noop)
      .finally(() => {
        this.isFinalisingSale = false
      })
  }

  /**
   * Responsible for discarding the current sale.
   * Only clears the sale if it's empty new sale, voids the sale in all other
   * situations
   * Then show the user switching lightbox if needed.
   */
  public discardSale() {
    if (this.saleManager.saleCalculating) {
      this.setSaleCalculatingState(true)
      return
    }

    this.isFinalisingSale = true

    this.trackSaleActionButton('Discard Sale')

    const _printReceipt = () => {
      const trackingEvent = 'Discard Sale and Print Receipt'

      if (this.trainingModeManager.trainingModeOnboardingState) {
        this.vdTracking.trackUserEvent({
          eventName: this.trainingModeManager.currentTourType!,
          metadata: {
            step: trackingEvent,
          },
        })
      } else {
        this.vdTracking.trackUserEvent({
          eventName: trackingEvent,
        })
      }

      return this.saleManager.getReceiptData().then(data => {
        $rootScope.$emit(EVENT.PRINT, {
          type: 'receipt-settings-and-template',
          data,
        })
      })
    }

    const conditionalDiscard = () => {
      const isNewSale = !this.currentSale.id

      if (!hasLineItems(this.currentSale) && isNewSale) {
        return $q.when(this.saleManager.clearSale())
      } else if (isNewSale && this.currentSale.payments.length === 0) {
        return $q.when(this.saleManager.clearSale())
      } else {
        // If the sale has payments, a sale receipt will be printed
        const printReceiptResolver = this.currentSale.payments.length
          ? _printReceipt()
          : $q.resolve()
        return printReceiptResolver.then(() => {
          this.saleManager.voidSale()
        })
      }
    }

    const lineItems = selectLineItems(this.$ngRedux.getState())
    this.emitDiscardSaleToClientApi(this.currentSale, lineItems)

    conditionalDiscard().then(() => {
      $q.when(this.userSwitchingService.switchUserIfNeeded())
        .then(() => this.focusGlobalSearch())
        .finally(() => {
          this.isFinalisingSale = false
        })
    })
  }

  /**
   * Opens the "retrieve sales" popover which shows the recent collection of parked sales.
   */
  public retrieveSale(event: any) {
    this.vdPopover.open({
      trigger: event.currentTarget,
      template: retrieveSaleTemplate,
      controller: 'RetrieveSalePopoverController',
      hasPopoverList: true,
      direction: 'center',
      openAsModalAtViewport: 'xsmall',
    })
  }

  /**
   * Establish if the current sale is in a state that can accept payments.
   *
   * @return {Boolean} True if the sale can accept payments, false otherwise
   */
  public preventTransitionToPaymentScreen(): boolean {
    const isSaleNoteInvalid = selectNoteIsInvalid(this.$ngRedux.getState())
    const isFulfilmentNoteInvalid = selectFulfilmentNoteIsInvalid(
      this.$ngRedux.getState()
    )
    const hasFulfilmentSaleAttributes = selectSaleAttributesHasFulfilment(
      this.$ngRedux.getState()
    )

    let hasInputErrors = this.saleForm.$invalid

    if (
      this.vdFeatureManager.get('fulfillment_lif_b3') &&
      (!hasInputErrors || this.saleOnlyHasNoInventoryError())
    ) {
      hasInputErrors = selectHasQuantityInputError(
        this.$ngRedux.getState<RootState>()
      )
    }
    return Boolean(
      this.isInitialising ||
        !this.hasActiveSale ||
        hasInputErrors ||
        !this.hasLineItems() ||
        this.saleCalculating ||
        this.isFinalisingSale ||
        this.waitingForWorkflows ||
        this.saleHasUnknownLineItems() ||
        this.isUnfulfilledSaleInvalidCustomer() ||
        this.isUnfulfilledSaleMissingContactDetailsCustomer() ||
        isSaleNoteInvalid ||
        (!hasFulfilmentSaleAttributes && isFulfilmentNoteInvalid)
    )
  }

  /**
   * Responsible for preparing the sale prior to accepting payments. Specifically ensuring we have an updated
   * customer - their most recent loyalty and account balances, etc.
   */
  public transitionToPaymentScreen(hasSelectedLspayQuickPay?: boolean) {
    if (
      (hasServiceOrder(
        this.currentSale.serviceFields,
        this.currentSale.customFields
      ) ||
        isLegacyServiceSale(this.currentSale.status)) &&
      this.getCompleteSaleLabel() === COMPLETE_SALE_LABELS.REFUND &&
      this.shouldOpenServiceSaleRefundModal
    ) {
      return this.openServiceSaleRefundModal()
    }

    if (this.preventTransitionToPaymentScreen()) {
      return
    }
    if (this.saleManager.saleCalculating) {
      this.setSaleCalculatingState(true)
      return
    }

    this.isFinalisingSale = true

    if (!this.trainingModeManager.trainingModeEnabled) {
      // time how long it takes to tender a sale
      this.vdPerformanceTracking.start('sale-process')

      // start timing how long it takes to initiate a sale with a payment type
      this.vdPerformanceTracking.start('select-payment-type-process')
    }
    let customerRefreshPromise = $q.resolve()
    if (isValidCustomer(this.currentSale.customer)) {
      customerRefreshPromise = $q.when(
        this.customerManager.refreshCustomer(this.currentSale.customer)
      )
    }

    const newAsOldSale = selectCurrentSaleAsSale(this.$ngRedux.getState())
    this.priceManager.totalSale(newAsOldSale)

    this.setFulfilmentTypeForUnfulfilledSale(
      this.vdFeatureManager.get('fulfillment_lif')
    )

    customerRefreshPromise
      .then(() => this.readyForPayment(hasSelectedLspayQuickPay))
      .finally(() => {
        this.$scope.$applyAsync(() => {
          this.isFinalisingSale = false
        })
      })
  }

  private setFulfilmentTypeForUnfulfilledSale(fulfilmentLif: boolean) {
    const sale = selectCurrentSaleAsSale(this.$ngRedux.getState())
    if (sale.status && isMarkedAsUnfulfilled(sale.status)) {
      const statusToFulfilmentType: {
        [key: string]: FulfilmentType
      } = {
        [SaleStatus.AWAITING_DISPATCH]: FulfilmentType.DELIVERY,
        [SaleStatus.AWAITING_PICKUP]: FulfilmentType.PICKUP,
      }

      const fulfilmentType = statusToFulfilmentType[sale.status]

      if (
        // When LIF is enabled, this in handled in the sale manager
        !fulfilmentLif
      ) {
        sale.lineItems.forEach(lineItem => {
          this.$ngRedux.dispatch(
            updateLineItem({ ...lineItem, fulfilmentType })
          )
        })
      }

      if (hasDiscount(sale.discount)) {
        const newDiscount = createSaleDiscount({
          ...sale.discount,
          fulfilmentType,
        })
        this.$ngRedux.dispatch(updateSaleDiscount({ discount: newDiscount }))
      }
    }
  }

  /**
   * Responsible for determining whether the user can transition to the pay screen
   * then transitioning if they meet the requirements.
   */
  private readyForPayment(hasSelectedLspayQuickPay?: boolean) {
    const completeAction = () => {
      $state.go(STATES.PAYMENT, {
        lspayQuickPaySelected: hasSelectedLspayQuickPay,
      })
    }

    this.waitingForWorkflows = true
    return this.workflowService
      .readyForPayment({
        element: document.getElementById('wr-pay-button')!,
        placement: 'top',
      })
      .then((proceed: boolean) => {
        if (proceed) {
          completeAction()
        }
      })
      .finally(() => {
        this.$scope.$applyAsync(() => {
          this.waitingForWorkflows = false
        })
      })
  }

  /**
   * Determine if the current sale has any line items
   *
   * @deprecated Use hasLineItems from sale helper functions
   */
  public hasLineItems() {
    return hasLineItems(this.currentSale)
  }

  /**
   * Check whether current user has the permission to set discount
   *
   * @return {Boolean} true if the user has permission
   */
  public hasPermissionToDiscount() {
    return this.userManager.canApplySaleDiscount()
  }

  // ============================== Private API ======================== //

  /**
   * Ensure the conditions are right to be selling. A register is open, there is an active sale that is not currently
   * in the process of being finalised (paid/parked/discarded).
   *
   * @returns <Boolean>
   */
  private canSell(): boolean {
    return Boolean(
      this.registerManager.isActiveRegisterOpen() &&
        this.currentSale &&
        !this.isFinalisingSale
    )
  }

  /**
   * Helper which evaluates and warns if the provided sale is an `On Account` or `Laby` sale made on an outlet thats
   * different from the current active outlet. We prevent these sales from being continued since inventory will
   * attributed to the original outlet.
   * @return <Promise>
   */
  private checkOutletOfLoadedSale(loadedSale: Sale) {
    // We only warn when the sale is either an `On Account` or `Laby` sale
    if (
      loadedSale.status !== SaleStatus.LAYBY_SALE &&
      loadedSale.status !== SaleStatus.ACCOUNT_SALE
    ) {
      return $q.resolve()
    }

    return this.registerManager
      .getRegisterById(loadedSale.registerId!)
      .then(register => {
        if (!register) {
          return
        }

        const activeOutlet = this.registerManager.getActiveOutlet()

        if (activeOutlet.id !== register.outlet_id) {
          return this.registerManager
            .getOutletById(register.outlet_id)
            .then((outlet: $TSFixMe) => {
              return this.saleModals.warnThatOutletsDiffer({
                saleOutletName: outlet.name,
                saleStatus: loadedSale.status,
              })
            })
        }
      })
  }

  /**
   * Helper to exit training mode if it is enabled.
   *
   * @return <Promise> Resolved once training mode has been exited
   */
  private exitTrainingModeIfNeeded() {
    return this.trainingModeManager.trainingModeEnabled
      ? this.trainingModeManager.exitTrainingMode()
      : $q.resolve()
  }

  private emitDiscardSaleToClientApi(
    currentSale: ReduxCurrentSale | Sale,
    lineItems: LineItem[]
  ) {
    clientApiEventBus.emitEvent({
      eventName: clientApiEventBus.EVENTS.SALE_DISCARD,
      data: { sale: this.saleTransformer.toClientApi(currentSale, lineItems) },
    })
  }

  /**
   * Set the sale calculating state
   * will set both line items and sale state for the sale controller
   */
  private setSaleCalculatingState(state: boolean) {
    this.$ngRedux.dispatch(updateCalculatingStateOfAllLineItems(state))
    this.saleCalculating = state
  }

  /**
   * Finalise the parking of a sale.
   *
   * @return <Promise> Resolved once the sale has been parked
   */
  private finaliseParkSale() {
    return $q
      .when(this.saleManager.parkSale())
      .then(() => $q.when(this.userSwitchingService.switchUserIfNeeded()))
      .then(() => this.focusGlobalSearch())
  }

  /**
   * Handles the search:product-selected event, passing inventory item to Sale Manager to be priced &
   * added to the Sale.
   *
   * @param event {Event}
   *        The custom broadcast event object.
   *
   * @param product {Object}
   *        Selected product.
   */
  private handleProductSelected(event: unknown, product: ApiProduct) {
    if (this.canSell()) {
      if (isGiftCard(product)) {
        this.balancesGiftCardsModal
          .sellGiftCard()
          .then(results => {
            this.saleManager.addGiftCardItem({
              product,
              code: results.code,
              balance: results.value,
              expiresAt: results.expiresAt,
              isReload: results.isReload,
              clientId: results.clientId,
            })
          })
          .catch(() => {
            this.saleManager.saleCalculating = false
          })
      } else {
        this.saleManager.addProductToSale(product)
      }
      this.focusGlobalSearch()
    } else {
      this.vdTracking.trackUserEvent({
        eventName: 'Show Product Information',
        metadata: {
          view: 'Register Closed Product Selected',
          isGiftCard: isGiftCard(product),
        },
      })
      this.productModals
        .viewDetails(product)
        .then(() => this.focusGlobalSearch())
    }
  }

  /**
   * Handles the search:unknown-product event; when an attempt to add an unknown product is made.
   *
   * @param {Event} event
   *        The custom broadcast event object.
   *
   * @param  {Object} data
   *
   *         @param {String} data.query
   *                The SKU/code that was searched for
   *
   *         @param {Array<Suggestion>} data.suggestions
   *                The suggestions returned by the feeling lucky search
   */
  private handleUnknownProductSelected(
    event: unknown,
    {
      query,
      suggestions,
    }: {
      query: string
      suggestions: any[]
    }
  ) {
    if (!this.canSell()) {
      return
    }

    if (!suggestions.length) {
      this.saleManager.addUnknownProductToSale(query)

      this.vdTracking.trackUserEvent({
        eventName: 'Unknown Product Added',
        metadata: {
          query,
        },
      })
    }
  }

  /**
   * Remove an unknown product from the sale.
   *
   * @param item {UnknownLineItem}
   *        The unknown line item to remove from the sale.
   */
  public removeUnknownProduct = (item: UnknownLineItem) => {
    this.$ngRedux.dispatch(removeUnknownLineItem(item.localId))
    this.saleManager.saveSale()
  }

  /**
   * Handles the search:embedded-barcode-selected event, passing product and set price
   *
   * @param event {Event}
   *        The custom broadcast event object.
   *
   * @param params {Object}
   *        Selected product and price
   */
  private handleEmbeddedBarcodeSelected(
    event: unknown,
    {
      product,
      barcodeData,
    }: {
      product: ApiProduct
      barcodeData: number
    }
  ) {
    if (this.canSell()) {
      let options: Partial<LineItemInput> = {}
      const barcodeOption = this.retailerSettingsManager.getBarcodeOption()
      if (barcodeOption === 'price') {
        options = { price: (barcodeData / 100).toString() }
      } else if (barcodeOption === 'weight') {
        options = { quantity: barcodeData / 1000 }
      }

      this.saleManager.addProductToSale(product, options, false)
      this.focusGlobalSearch()
    }
  }

  /**
   * Handles the search:customer-selected event, adding the customer to the Sale.
   *
   * @param  event {Event}
   *         The event details
   *
   * @param  customer {Customer}
   *         The customer selected
   */
  private async handleCustomerSelected(event: unknown, customer: ApiCustomer) {
    if (this.canSell()) {
      if (isConfirmedNotReadyToComplete(this.currentSale)) {
        const statusDisplayName = this.wrSaleStatusFilter(
          this.currentSale.status,
          ''
        )
        const retrievedSaleMessage = `You are currently viewing a retrieved ${statusDisplayName} sale.
            The customer can not be changed.`

        this.vdToastNotificationService.negative({
          message: retrievedSaleMessage,
        })
      } else if (hasCustomerRelatedPayments(this.currentSale.payments)) {
        this.vdToastNotificationService.negative({
          message: PARTIALLY_PAID_MESSAGE,
        })
      } else {
        // Ensure we have the latest details before adding the customer to the sale
        const latest = await this.customerManager.refreshCustomer(customer)
        await this.saleManager.addCustomer(latest as ApiCustomer)
      }
      $rootScope.$emit('autocomplete:global-search:focus')
    } else {
      this.vdTracking.trackUserEvent({
        eventName: 'Show Customer Information',
        metadata: {
          view: 'Register Closed Customer Selected',
        },
      })
      this.customerModals.viewCustomer(customer)
    }
  }

  /**
   * Handles the sale:remove-line-item event
   *
   * @param event {Event}
   *        The custom emit event object.
   *
   * @param lineItem {LineItem}
   *        The Line Item to be removed from the Sale.
   */
  private handleRemoveLineItem(event: unknown, lineItem: LineItem) {
    this.saleManager.removeLineItem(lineItem)
    this.focusGlobalSearch()
  }

  /**
   * Responsible for focusing the global search.
   */
  private focusGlobalSearch() {
    $timeout(() => $rootScope.$emit('autocomplete:global-search:focus'))
  }

  public focusCustomerSearch = () => {
    $rootScope.$emit('autocomplete:customer-search:focus', {
      isFocusable: true,
    })
  }

  /**
   * Track sale action buttons.
   *
   * @param  {String} saleActionButton
   *         The button that was clicked.
   */
  private trackSaleActionButton(saleActionButton: string) {
    this.vdTracking.trackUserEvent({
      eventName: 'Sale Action Buttons',
      metadata: {
        saleState: this.currentSale.displayStatus,
        button: saleActionButton,
      },
    })
  }

  /**
   * Determine if the open register reminder needs to be shown.
   */
  private isRegisterOpenReminderNeeded() {
    $log.debug('[SaleController] Register open reminder check')

    this.showRegisterOpenReminder = this.registerManager.isRegisterOpenReminderNeeded()

    if (
      this.showRegisterOpenReminder ||
      this.registerManager.isRegisterOpenReminderSeen()
    ) {
      this.cancelPollForExpectedRegisterClosure()
    }
  }

  /**
   * Polls to check if the register open reminder needs to be shown.
   */
  private pollForExpectedRegisterClosure() {
    if (!this.expectedRegisterClosureInterval) {
      this.expectedRegisterClosureInterval = $interval(
        () => this.isRegisterOpenReminderNeeded(),
        expectedRegisterClosurePollTime
      )
    }

    // Check immediately
    this.isRegisterOpenReminderNeeded()
  }

  /**
   * Cancel the $interval poll used to check if the register was opened today.
   */
  private cancelPollForExpectedRegisterClosure() {
    if (this.expectedRegisterClosureInterval) {
      $log.debug('[SaleController] Cancel open register reminder poll')
      $interval.cancel(this.expectedRegisterClosureInterval)
      this.expectedRegisterClosureInterval = null
    }
  }

  /**
   * Toggles the expected register closure poll based on either the register being offline or if
   * training mode is enabled. Since in both these cases, the register can not be closed.
   */
  private toggleExpectedRegisterClosurePoll() {
    if (
      !this.trainingModeManager.trainingModeEnabled &&
      this.webRegisterStatusManager.isOnline()
    ) {
      this.pollForExpectedRegisterClosure()
    } else {
      this.cancelPollForExpectedRegisterClosure()
      this.showRegisterOpenReminder = false
    }
  }

  /**
   * Ensures the sale form is in a clean state for each new sale.
   */
  private resetSaleForm() {
    if (this.saleForm) {
      $log.debug('[SaleController] Resetting sale form')
      this.saleForm.$setPristine()
      this.saleForm.$setUntouched()
      this.saleForm.$error = {}
      this.saleForm.$invalid = false
      this.saleForm.$valid = true

      this.$ngRedux.dispatch(resetLineItemInputErrors())
    }
  }

  /**
   * Check if the Lspay payment type is available
   */
  public async checkLspayPaymentTypeAvailability() {
    this.hasLspayPaymentType = await this.paymentManager.hasLspayPaymentType()
  }

  /**
   * Check if the Lspay Quick Pay button needs to be disabled
   */
  public shouldDisableLspayQuickPayButton() {
    const outStandingBalance = getOutstandingBalance(this.currentSale)
    return (
      !this.webRegisterStatusManager.isOnline() ||
      this.preventTransitionToPaymentScreen() ||
      vn(outStandingBalance).lte(0)
    )
  }

  /**
   * Check if the Lspay Quick Pay button needs to be displayed
   */
  public shouldDisplayLspayQuickPayButton() {
    return (
      this.vdFeatureManager.get('lspay_pay_with_quick_pay_experiment') &&
      this.hasLspayPaymentType &&
      this.currentSale &&
      !this.trainingModeManager.trainingModeEnabled
    )
  }

  /**
   * Handles Lspay quick pay button onClick
   */
  public lspayQuickPay() {
    // send an ube when the quick pay button is clicked
    this.vdTracking.trackUserEvent({
      eventName: 'Payment type selected',
      metadata: {
        amount: getOutstandingBalance(this.currentSale),
        paymentTypeId: PAYMENT_TYPES.LSPAY,
        registerSaleId: this.currentSale.localId,
        registerId: this.registerManager.getActiveRegister().id,
        isQuickPay: true,
        quickPayFeatureFlag: this.vdFeatureManager.get(
          'lspay_pay_with_quick_pay_experiment'
        ),
      },
    })
    // Passing true to indicate that the Lspay Quick Pay button was clicked
    this.transitionToPaymentScreen(true)
  }

  public excludeFulFilmentItems(item: LineItem) {
    return (
      item.fulfilmentType === undefined ||
      item.fulfilmentType === FulfilmentType.UNSPECIFIED
    )
  }

  public isFulfilmentLifEnabled() {
    return this.vdFeatureManager.get('fulfillment_lif')
  }

  public showSection(sectionType: string) {
    const sale = selectCurrentSaleAsSale(this.$ngRedux.getState())
    const concatenatedLineItems = [...sale.lineItems, ...sale.unknownLineItems]

    const hasDeliveryOrPickupItems = concatenatedLineItems.some(
      item =>
        'fulfilmentType' in item &&
        (item.fulfilmentType === FulfilmentType.DELIVERY ||
          item.fulfilmentType === FulfilmentType.PICKUP)
    )

    const hasUnspecifiedOrUndefinedFulfilment = concatenatedLineItems.some(
      item =>
        'fulfilmentType' in item
          ? item.fulfilmentType === undefined ||
            item.fulfilmentType === FulfilmentType.UNSPECIFIED
          : true
    )

    const hasDeliveryItems = concatenatedLineItems.some(item =>
      'fulfilmentType' in item
        ? item.fulfilmentType === FulfilmentType.DELIVERY
        : false
    )

    const hasPickupItems = concatenatedLineItems.some(item =>
      'fulfilmentType' in item
        ? item.fulfilmentType === FulfilmentType.PICKUP
        : false
    )

    switch (sectionType) {
      case 'TAKENOW':
        return hasDeliveryOrPickupItems && hasUnspecifiedOrUndefinedFulfilment
      case 'DISPATCH':
        return hasDeliveryItems
      case 'PICKUP':
        return hasPickupItems
      default:
        return false
    }
  }

  /**
   * Wrapper for the helper function to determine if the sale has a service
   * order
   */
  public hasServiceOrder() {
    return hasServiceOrder(
      this.currentSale.serviceFields,
      this.currentSale.customFields
    )
  }

  public showActionsAndForm() {
    return (
      this.registerManager.isActiveRegisterOpen() &&
      (!this.showRegisterOpenReminder || this.hasActiveSale)
    )
  }

  public isConfirmedNotReadyToComplete() {
    return isConfirmedNotReadyToComplete(this.currentSale)
  }

  public isMarkedAsUnfulfilled() {
    return isMarkedAsUnfulfilled(this.currentSale.status)
  }

  public isValidCustomer() {
    return isValidCustomer(this.currentSale.customer)
  }

  public isUnfulfilledSaleInvalidCustomer() {
    return (
      this.vdFeatureManager.get('fulfillment_lif_b3') &&
      this.isMarkedAsUnfulfilled() &&
      !this.isValidCustomer()
    )
  }

  public isUnfulfilledSaleMissingContactDetailsCustomer() {
    if (this.vdFeatureManager.get('fulfillment_lif_bug_fixes')) {
      return (
        this.vdFeatureManager.get('fulfillment_lif_b3') &&
        this.isMarkedAsUnfulfilled() &&
        isValidCustomer(this.currentSale.customer) &&
        isMissingContactInfo(this.currentSale.customer)
      )
    }
    return (
      this.vdFeatureManager.get('fulfillment_lif_b3') &&
      this.isMarkedAsUnfulfilled() &&
      this.currentSale.customer &&
      isMissingContactInfo(this.currentSale.customer)
    )
  }

  public isFulfillmentPopoverOpen() {
    return selectIsFulfilmentPopoverOpen(this.$ngRedux.getState<RootState>())
  }

  public isUnfulfilledPickUp() {
    return isUnfulfilledPickUp(this.currentSale.status)
  }

  public getPaymentsTotal() {
    return getPaymentsTotal(this.currentSale.payments)
  }

  public getOutstandingBalance() {
    return getOutstandingBalance(this.currentSale)
  }

  public getBasketSize() {
    const lineItems = selectLineItems(this.$ngRedux.getState())
    return getBasketSize(lineItems)
  }
}

/**
 * Represents a sale element of line items & totals.
 *
 * @module webregister.controllers.sale
 */
angular.module('webregister.controllers').controller('SaleCtrl', SaleCtrl)
